import React from 'react';
import { isEmpty } from 'lodash';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import {
    ArrayOfObjectsV2,
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';

type DataListingItem = {
    Name?: string;
    IsActive?: boolean;
    DatatableId?: {
        $oid: string;
    };
    DisplayIndex?: number;
    RecordId?: number;
};

type DataViewConfig = {
    DataListingsItems: Partial<Array<unknown>>;
    CfId: {
        $oid: string;
    };
    A: {
        $oid: string;
    };
    D: boolean;
    H: number;
    T: {
        $date: string;
    };
    AuditDescription?: string;
    AuditDescriptionMessage?: string;
};

type DataListingsChangeGridProps = {
    oldRecord: Partial<DataViewConfig>;
    newRecord: Partial<DataViewConfig>;
    oldItem?: Record<string, unknown>;
    newItem?: Record<string, unknown>;
};

const SMDataListingsConfigRecordChangeGrid = ({
    oldRecord,
    newRecord,
    oldItem,
    newItem,
}: DataListingsChangeGridProps) => {
    if (isEmpty(oldRecord) && isEmpty(newRecord)) return null;

    // Lots of ID fields to hide
    const hiddenFields: string[] = ['CfId', 'AuditDescription', 'AuditDescriptionMessage'];

    const arrayOfObjectsV2: ArrayOfObjectsV2 = {
        DataListingsItems: {
            valueResolver: (recordData: unknown) => {
                if (!recordData) {
                    return null;
                }
                const data = recordData as DataListingItem & { index: number };

                return `${data.index + 1}: SM-${data.RecordId}: ${data.Name}`;
            },
            // Compare items at the same index and see if their record id has changed
            compareField: 'index',
            valueComparer: (oldObject, newObject) => {
                // Try to get record id out of object, if defined
                const oldRecordId = oldObject instanceof Object && 'RecordId' in oldObject ? oldObject.RecordId : null;
                const newRecordId = newObject instanceof Object && 'RecordId' in newObject ? newObject.RecordId : null;

                return oldRecordId !== newRecordId; // True means display. False means don't...
            },
        },
    };

    const refactoredOldRecord = {
        ...oldRecord,
        ...removePropertiesFromObject(oldRecord, hiddenFields),
        // Add index to each defined data-listing item
        DataListingsItems: oldRecord.DataListingsItems?.map((x, index) => (x != null ? { ...x, index } : null)),
    } as Record<string, unknown>;

    const refactoredNewRecord = {
        ...newRecord,
        ...removePropertiesFromObject(newRecord, hiddenFields),
        // Add index to each defined data-listing item
        DataListingsItems: newRecord.DataListingsItems?.map((x, index) => (x != null ? { ...x, index } : null)),
    } as Record<string, unknown>;

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            arrayOfObjectsV2,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { SMDataListingsConfigRecordChangeGrid };
